// @flow

import React, { useMemo } from 'react';
import CategorizedPickerPopover from '../pills/categorized-picker-popover';
import type { Option } from '../pills/categorized-picker-popover';
import type { TagTypeMeta } from './tag-types';

type Props = {
  location: Object,
  allTags: $ReadOnlyArray<Object>,
  tagTypes: TagTypeMeta[],
  disabled?: boolean,
  tooltip?: string,
  onAdd: (tag: Object) => mixed,
  size?: 'sm' | 'md',
};

const RowTagEditor = ({
  location,
  allTags,
  tagTypes,
  disabled,
  tooltip,
  onAdd,
  size = 'sm',
}: Props) => {
  const locationTagIds = useMemo(
    () => new Set(location.tags || []),
    [location],
  );

  const categories = useMemo(
    () => tagTypes.map((tt) => ({ key: tt.key, label: tt.label, color: tt.color })),
    [tagTypes],
  );

  const options = useMemo(
    () =>
      allTags.map((t) => ({
        id: t.id,
        label: t.tagValue,
        categoryKey: t.tagType,
      })),
    [allTags],
  );

  const conflictsByCategory = useMemo(() => {
    const out: { [string]: ?{ valueLabel: string } } = {};
    tagTypes.forEach((tt) => {
      const existing = allTags.find(
        (t) => locationTagIds.has(t.id) && t.tagType === tt.key,
      );
      out[tt.key] = existing ? { valueLabel: existing.tagValue } : null;
    });
    return out;
  }, [allTags, locationTagIds, tagTypes]);

  const singleSelectCategories = useMemo(
    () => tagTypes.map((tt) => tt.key),
    [tagTypes],
  );

  const excludeIds = useMemo(
    () => Array.from(locationTagIds),
    [locationTagIds],
  );

  const handlePick = (opt: Option) => {
    const tag = allTags.find((t) => t.id === opt.id);
    if (tag) onAdd(tag);
  };

  return (
    <CategorizedPickerPopover
      categories={categories}
      options={options}
      excludeIds={excludeIds}
      singleSelectCategories={singleSelectCategories}
      conflictsByCategory={conflictsByCategory}
      size={size}
      disabled={disabled}
      tooltip={tooltip}
      addLabel="Add tag"
      categoryHeaderLabel="Choose tag type"
      onPick={handlePick}
    />
  );
};

export default RowTagEditor;
